/** Document types that open the image cropper before upload (image-only). */
export const IMAGE_CROP_DOCUMENT_TYPES = new Set(['photo']);

export const MAX_IMAGE_UPLOAD_BYTES = 5 * 1024 * 1024; // 5MB — matches existing upload limit
export const DEFAULT_CROP_ASPECT = 1;
export const DEFAULT_CROP_ZOOM = 1;
export const DEFAULT_CROP_ROTATION = 0;
export const MIN_CROP_ZOOM = 1;
export const MAX_CROP_ZOOM = 3;

const IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

/**
 * True when the file is an image the cropper can load (not PDF/docs).
 */
export function isImageFile(file) {
  if (!file) return false;
  const type = String(file.type || '').toLowerCase();
  if (type === 'application/pdf') return false;
  if (IMAGE_MIME_TYPES.has(type) || type.startsWith('image/')) {
    return !type.includes('svg');
  }
  const name = String(file.name || '').toLowerCase();
  if (name.endsWith('.pdf')) return false;
  return IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
}

/**
 * Whether selecting this file for the document type should open the cropper.
 * KYC PDFs and mixed-format docs skip cropping.
 */
export function shouldCropDocumentUpload(documentType, file) {
  if (!IMAGE_CROP_DOCUMENT_TYPES.has(documentType)) return false;
  return isImageFile(file);
}

export function revokeObjectUrl(url) {
  if (url && String(url).startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

export function createObjectUrl(fileOrBlob) {
  return URL.createObjectURL(fileOrBlob);
}

export function resolveOutputMimeType(file) {
  const type = String(file?.type || '').toLowerCase();
  if (type === 'image/png') return 'image/png';
  if (type === 'image/webp') return 'image/webp';
  return 'image/jpeg';
}

export function resolveOutputFileName(originalName, mimeType) {
  const base = String(originalName || 'cropped-image').replace(/\.[^.]+$/, '') || 'cropped-image';
  if (mimeType === 'image/png') return `${base}.png`;
  if (mimeType === 'image/webp') return `${base}.webp`;
  return `${base}.jpg`;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', () => reject(new Error('Failed to load image for cropping.')));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = src;
  });
}

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * Produce a cropped image Blob from an object URL / data URL and pixel crop area.
 */
export async function getCroppedImageBlob(
  imageSrc,
  pixelCrop,
  {
    rotation = 0,
    mimeType = 'image/jpeg',
    quality = 0.92,
  } = {},
) {
  if (!imageSrc || !pixelCrop?.width || !pixelCrop?.height) {
    throw new Error('Invalid crop area.');
  }

  const image = await loadImage(imageSrc);
  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not supported in this browser.');

  canvas.width = Math.max(1, Math.round(bBoxWidth));
  canvas.height = Math.max(1, Math.round(bBoxHeight));

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement('canvas');
  const croppedCtx = croppedCanvas.getContext('2d');
  if (!croppedCtx) throw new Error('Canvas is not supported in this browser.');

  const width = Math.max(1, Math.round(pixelCrop.width));
  const height = Math.max(1, Math.round(pixelCrop.height));
  croppedCanvas.width = width;
  croppedCanvas.height = height;

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    width,
    height,
    0,
    0,
    width,
    height,
  );

  const outputType = mimeType === 'image/jpg' ? 'image/jpeg' : mimeType;
  const blob = await new Promise((resolve, reject) => {
    croppedCanvas.toBlob(
      (result) => {
        if (!result) reject(new Error('Failed to export cropped image.'));
        else resolve(result);
      },
      outputType,
      outputType === 'image/png' ? undefined : quality,
    );
  });
  return blob;
}

/**
 * Export cropped File under maxBytes by stepping down JPEG/WebP quality when needed.
 */
export async function getCroppedImageFile(
  imageSrc,
  pixelCrop,
  {
    fileName,
    mimeType = 'image/jpeg',
    rotation = 0,
    maxBytes = MAX_IMAGE_UPLOAD_BYTES,
  } = {},
) {
  const outputMime = mimeType === 'image/jpg' ? 'image/jpeg' : mimeType;
  const outputName = resolveOutputFileName(fileName, outputMime);
  const qualities = outputMime === 'image/png' ? [undefined] : [0.92, 0.85, 0.75, 0.65, 0.55];

  let lastBlob = null;
  for (const quality of qualities) {
    lastBlob = await getCroppedImageBlob(imageSrc, pixelCrop, {
      rotation,
      mimeType: outputMime,
      quality,
    });
    if (!maxBytes || lastBlob.size <= maxBytes) {
      return new File([lastBlob], outputName, {
        type: outputMime,
        lastModified: Date.now(),
      });
    }
  }

  throw new Error(`Cropped image exceeds the ${Math.round(maxBytes / (1024 * 1024))}MB size limit.`);
}
